import { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GetRowIdParams } from 'ag-grid-community';
import { useAgGridStatePersistence } from './useAgGridStatePersistence';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

export const HORIZONTAL_RULES_AG_GRID_CSS = `
.doc-settings-hrules-ag-grid .ag-root-wrapper,
.doc-settings-hrules-ag-grid .ag-root-wrapper-body { border: none !important; }
.doc-settings-hrules-ag-grid .ag-cell,
.doc-settings-hrules-ag-grid .ag-header-cell {
  border-left: none !important; border-right: none !important; border-top: none !important;
}
.doc-settings-hrules-ag-grid .ag-header-cell { border-bottom: 1px solid rgb(229 231 235) !important; }
.doc-settings-hrules-ag-grid .ag-header { border-bottom: none !important; }
.doc-settings-hrules-ag-grid .ag-row .ag-cell { border-bottom: 1px solid rgb(243 244 246) !important; }
.dark .doc-settings-hrules-ag-grid .ag-header-cell,
.dark .doc-settings-hrules-ag-grid .ag-row .ag-cell { border-bottom-color: var(--cty-border) !important; }
.doc-settings-hrules-ag-grid .ag-layout-auto-height .ag-center-cols-viewport,
.doc-settings-hrules-ag-grid .ag-layout-auto-height .ag-center-cols-container { min-height: 0 !important; }
.doc-settings-hrules-ag-grid .ag-cell-wrapper { align-items: flex-start; }
.doc-settings-hrules-ag-grid .ag-cell-ellipsis,
.doc-settings-hrules-ag-grid .ag-cell-ellipsis .ag-cell-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.doc-settings-hrules-ag-grid .ag-row:not(.ag-row-auto-height) .ag-cell-wrapper {
  align-items: center;
}
`;

const ELLIPSIS_CELL_STYLE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export { ELLIPSIS_CELL_STYLE };

export function HorizontalRulesAgGrid<TData>({
  rowData,
  columnDefs,
  getRowId,
  context,
  dense = false,
  gridPersistenceKey,
  getRowClass,
  resizable = false,
  wrapText = true,
  autoHeight = true,
  sizeColumnsToFitOnReady = true,
}: Readonly<{
  rowData: TData[];
  columnDefs: ColDef<TData>[];
  getRowId: (params: GetRowIdParams<TData>) => string;
  context?: unknown;
  dense?: boolean;
  gridPersistenceKey?: string;
  getRowClass?: (params: { data?: TData }) => string;
  resizable?: boolean;
  wrapText?: boolean;
  autoHeight?: boolean;
  sizeColumnsToFitOnReady?: boolean;
}>) {
  const persistenceKey = gridPersistenceKey ?? 'horizontal-rules-ag-grid';
  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
    useAgGridStatePersistence(persistenceKey);
  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);
  const fixedRowHeight = dense ? 44 : 52;

  const onGridReady = useCallback(
    (params: { api: { sizeColumnsToFit?: () => void } }) => {
      augmentGridReady(params, (p) => {
        if (sizeColumnsToFitOnReady) {
          p?.api?.sizeColumnsToFit?.();
        }
      });
    },
    [augmentGridReady, sizeColumnsToFitOnReady]
  );

  return (
    <div className="overflow-x-auto doc-settings-hrules-ag-grid">
      <style>{HORIZONTAL_RULES_AG_GRID_CSS}</style>
      <div className="ag-theme-alpine w-full" style={{ width: '100%' }}>
        <AgGridReact<TData>
          rowData={rowData}
          columnDefs={columnDefsWithSort}
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          context={context}
          defaultColDef={{
            sortable: false,
            resizable,
            suppressMovable: true,
            headerClass: dense
              ? 'text-left !py-2 !px-3 font-medium text-foreground'
              : 'text-left !py-3 !px-4 font-medium text-foreground',
            cellClass: dense ? '!py-2 !px-3' : '!py-3 !px-4',
            wrapText,
            autoHeight,
          }}
          domLayout="autoHeight"
          pagination={false}
          suppressPaginationPanel
          tooltipShowDelay={400}
          enableBrowserTooltips
          headerHeight={dense ? 40 : 48}
          rowHeight={autoHeight ? undefined : fixedRowHeight}
          getRowId={getRowId}
          getRowClass={getRowClass}
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
}
